import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Button,
  Typography,
  Card,
  Box,
  Stack,
  IconButton,
  Chip,
  InputAdornment,
  OutlinedInput,
  FormHelperText,
  Avatar,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import SelectComponent from 'src/components/SelectComponent';
import { financialSimulationService } from 'src/services/financial-simulation.service';
import { studentService } from 'src/services/student.service';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAuth } from 'src/context/auth/AuthContext';
import Page from 'src/components/Page';

const PRESET_DURATIONS = [3, 5, 7, 14, 30];

const assignNewChallengeValidationSchema = Yup.object().shape({
  selectedStudents: Yup.object().nullable().required('Please select a scholar'),
  scenario: Yup.object().nullable().required('Please select a scenario'),
  duration: Yup.number().required('Duration is required').positive('Must be at least 1 day'),
  amount: Yup.number().required('Amount is required').positive('Amount must be positive'),
});

export default function AssignNewChallenge() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: studentId } = useParams();
  const { user, hasPermission } = useAuth();

  const canViewAllStudents = hasPermission('students.view_all');

  const { data: studentsData, isLoading: isLoadingStudents } = useQuery({
    queryKey: ['active-students-assign', { teacherUserId: !canViewAllStudents ? (user?.id || user?.email) : undefined }],
    queryFn: () =>
      studentService.getActiveStudents({
        teacherUserId: !canViewAllStudents ? (user?.id || user?.email) : undefined,
      }),
    select: (response) =>
      response.map((student) => ({
        label: `${student.name} (${student.email})`,
        value: student.studentId,
        name: student.name,
        email: student.email,
      })),
  });

  const { data: scenariosData, isLoading: isLoadingScenarios } = useQuery({
    queryKey: ['scenarios-options'],
    queryFn: () => financialSimulationService.getAllScenarioOptions(),
    select: (response) =>
      response.map((scenario) => ({
        label: `${scenario.title} ($${Number(scenario.amount || 0).toFixed(2)})`,
        value: scenario.id,
        title: scenario.title,
        amount: scenario.amount,
      })),
  });

  const assignMutation = useMutation({
    mutationFn: (values: any) =>
      financialSimulationService.assignChallenge({
        studentId: values.selectedStudents?.value,
        scenarioId: values.scenario.value,
        amount: String(values.amount),
        duration: String(values.duration),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      queryClient.invalidateQueries({ queryKey: ['whammies-metrics'] });
      toast.success('Whammy challenge assigned successfully!');
      navigate(-1);
    },
    onError: (error: any) => {
      console.error('Error assigning challenge:', error);
      toast.error(error.message || 'Failed to assign challenge');
    },
  });

  const formik = useFormik({
    initialValues: {
      selectedStudents: null as any,
      scenario: null as any,
      duration: '7',
      amount: '',
    },
    validationSchema: assignNewChallengeValidationSchema,
    onSubmit: (values) => {
      assignMutation.mutate(values);
    },
  });

  useEffect(() => {
    if (studentsData && studentId) {
      const defaultStudent = studentsData.find((student) => student.value === studentId);
      if (defaultStudent) {
        formik.setFieldValue('selectedStudents', defaultStudent);
      }
    }
  }, [studentsData, studentId]);

  const handleScenarioSelect = (selectedScenario: any) => {
    formik.setFieldValue('scenario', selectedScenario);
    if (selectedScenario?.amount !== undefined) {
      formik.setFieldValue('amount', selectedScenario.amount);
    }
  };

  const selectedStudent = formik.values.selectedStudents;
  const selectedScenario = formik.values.scenario;
  const configuredAmount = Number(formik.values.amount) || 0;
  const configuredDuration = formik.values.duration || 7;

  return (
    <Page title="Assign Whammy Challenge | Gold Minds Portal">
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        {/* Top Navigation */}
        <Box sx={{ mb: 2, mt: 0.25 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <IconButton
              size="small"
              onClick={() => navigate(-1)}
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.75,
                p: 0.75,
              }}
            >
              <Icon icon="solar:arrow-left-linear" width={18} />
            </IconButton>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: -0.3, fontSize: '1.35rem' }}>
                Assign Whammy Challenge
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                Test scholar financial resilience by assigning an unexpected expense or budget simulation.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            {/* Form Column */}
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, fontSize: '0.95rem' }}>
                  Challenge Configuration
                </Typography>

                <Stack spacing={2}>
                  {/* Select Scholar */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary', display: 'block', fontSize: '0.75rem' }}>
                      Target Scholar *
                    </Typography>
                    <SelectComponent
                      label=""
                      options={studentsData || []}
                      isMulti={false}
                      placeholder="Search and select scholar..."
                      value={formik.values.selectedStudents}
                      onChange={(selectedOption) => formik.setFieldValue('selectedStudents', selectedOption)}
                      error={Boolean(formik.touched.selectedStudents && formik.errors.selectedStudents)}
                      helperText={formik.touched.selectedStudents && formik.errors.selectedStudents}
                      isLoading={isLoadingStudents}
                    />
                  </Box>

                  {/* Select Scenario */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary', display: 'block', fontSize: '0.75rem' }}>
                      Whammy Scenario *
                    </Typography>
                    <SelectComponent
                      label=""
                      options={scenariosData || []}
                      placeholder="Select a simulation scenario template..."
                      value={formik.values.scenario}
                      onChange={handleScenarioSelect}
                      error={Boolean(formik.touched.scenario && formik.errors.scenario)}
                      helperText={formik.touched.scenario && formik.errors.scenario}
                      isLoading={isLoadingScenarios}
                    />
                  </Box>

                  {/* Amount Field */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary', display: 'block', fontSize: '0.75rem' }}>
                      Penalty Amount ($) *
                    </Typography>
                    <OutlinedInput
                      fullWidth
                      size="small"
                      name="amount"
                      type="number"
                      placeholder="0.00"
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      startAdornment={<InputAdornment position="start"><span style={{ fontSize: '0.8125rem' }}>$</span></InputAdornment>}
                      error={Boolean(formik.touched.amount && formik.errors.amount)}
                      disabled={assignMutation.isPending}
                      sx={{ borderRadius: 1.75, fontSize: '0.8125rem', height: 38 }}
                    />
                    {formik.touched.amount && formik.errors.amount && (
                      <FormHelperText error sx={{ ml: 0.5, mt: 0.25, fontSize: '0.6875rem' }}>
                        {formik.errors.amount}
                      </FormHelperText>
                    )}
                  </Box>

                  {/* Duration in Days */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary', display: 'block', fontSize: '0.75rem' }}>
                      Resolution Duration (Days) *
                    </Typography>
                    <OutlinedInput
                      fullWidth
                      size="small"
                      name="duration"
                      type="number"
                      placeholder="e.g. 7"
                      value={formik.values.duration}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      endAdornment={<InputAdornment position="end"><span style={{ fontSize: '0.8125rem' }}>Days</span></InputAdornment>}
                      error={Boolean(formik.touched.duration && formik.errors.duration)}
                      disabled={assignMutation.isPending}
                      sx={{ borderRadius: 1.75, fontSize: '0.8125rem', height: 38 }}
                    />
                    {formik.touched.duration && formik.errors.duration && (
                      <FormHelperText error sx={{ ml: 0.5, mt: 0.25, fontSize: '0.6875rem' }}>
                        {formik.errors.duration}
                      </FormHelperText>
                    )}

                    {/* Preset Duration Chips */}
                    <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center', mr: 0.25, fontWeight: 600, fontSize: '0.6875rem' }}>
                        Duration Presets:
                      </Typography>
                      {PRESET_DURATIONS.map((dur) => (
                        <Chip
                          key={dur}
                          label={`${dur} Days`}
                          size="small"
                          onClick={() => formik.setFieldValue('duration', dur)}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: 24,
                            borderRadius: 1.25,
                            bgcolor: Number(formik.values.duration) === dur ? 'primary.main' : 'grey.100',
                            color: Number(formik.values.duration) === dur ? 'primary.contrastText' : 'text.primary',
                            '&:hover': {
                              bgcolor: Number(formik.values.duration) === dur ? 'primary.dark' : 'grey.200',
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>

                {/* Action Buttons */}
                <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(-1)}
                    disabled={assignMutation.isPending}
                    sx={{
                      borderRadius: 1.75,
                      px: 2,
                      height: 36,
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      textTransform: 'none',
                      color: 'text.secondary',
                      borderColor: 'divider',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    disabled={
                      assignMutation.isPending ||
                      !formik.values.selectedStudents ||
                      !formik.values.scenario ||
                      !formik.values.amount ||
                      !formik.values.duration
                    }
                    sx={{
                      borderRadius: 1.75,
                      px: 2.5,
                      height: 36,
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      textTransform: 'none',
                      bgcolor: 'primary.main',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                    {assignMutation.isPending ? 'Assigning Challenge...' : 'Assign Challenge'}
                  </Button>
                </Stack>
              </Card>
            </Grid>

            {/* Live Assignment Card Preview */}
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  bgcolor: 'grey.50',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
                  <Icon icon="solar:eye-bold-duotone" width={16} style={{ color: '#D97706' }} />
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                    Assignment Preview
                  </Typography>
                </Stack>

                <Card
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px dashed',
                    borderColor: selectedStudent && selectedScenario ? 'primary.main' : 'divider',
                    boxShadow: 'none',
                  }}
                >
                  {/* Scholar Badge */}
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                        fontWeight: 700,
                        fontSize: '0.8125rem',
                      }}
                    >
                      {(selectedStudent?.name || 'S').charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.8125rem', lineHeight: 1.2 }}>
                        {selectedStudent?.name || 'Select a Scholar'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                        {selectedStudent?.email || 'Scholar email will appear here'}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Challenge Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 1.25,
                          bgcolor: 'rgba(237, 180, 46, 0.15)',
                          color: '#D97706',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon icon="solar:danger-triangle-bold" width={15} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.8125rem' }}>
                        {selectedScenario?.title || 'Scenario Title'}
                      </Typography>
                    </Stack>
                    <Chip
                      label="In Progress"
                      size="small"
                      sx={{
                        height: 20,
                        fontWeight: 700,
                        fontSize: '0.6875rem',
                        bgcolor: 'rgba(245, 158, 11, 0.12)',
                        color: '#D97706',
                        borderRadius: 1.25,
                      }}
                    />
                  </Stack>

                  {/* Challenge Details Grid */}
                  <Grid container spacing={1} sx={{ my: 1.5 }}>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                          Penalty Amount
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#DC2626', mt: 0.25, fontSize: '1.05rem' }}>
                          ${configuredAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.6875rem' }}>
                          Duration
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', mt: 0.25, fontSize: '1.05rem' }}>
                          {configuredDuration} Days
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', textAlign: 'center', fontSize: '0.6875rem' }}>
                    Scholars can resolve this challenge via Scholar Dollar payments or active loan workflows.
                  </Typography>
                </Card>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Container>
    </Page>
  );
}
